// ThreadsPatrolOpportunities — the 機會 tab of /threads-patrol.
//
// Lists every Threads post the workspace screener (scripts/threads/patrol_screener.py)
// judged worth answering: someone asking where to buy men's clothes, what to
// wear, what to get a boyfriend. A post stays here while it still fits — until
// anyone logs a reply to it, someone marks it 不適合, or it is a week old (the
// backend applies all three, routes/threadsPatrol.routes.js GET /opportunities).
//
// The screener only reads Threads. Staff still answer by hand from their own
// accounts. Each card carries the screener's draft (editable — it is a starting
// point, the log keeps what was really sent) and, on score-3 store/item asks only,
// up to two matched in-stock products to attach. 我回了 logs the reply with the card's
// draft and drops the card at once — no trip to the 記錄 form (Magnus 2026-10-28:
// 「回了就直接從列表拿掉」). The log row still matters: it feeds 今天 x/5 and the
// duplicate check that stops two staff answering the same post.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import api from '../services/api';

export interface PatrolProduct {
  id: number;
  title: string;
  price: number;
  image: string | null;
  url: string;
}

export interface PatrolOpportunity {
  post_key: string;
  post_url: string;
  post_author: string | null;
  post_text: string | null;
  kind: 'post' | 'comment';
  parent_url: string | null;
  like_count: number;
  reply_count: number;
  shop_reply_count: number | null;
  posted_at: string;
  score: number;
  opp_type: string | null;
  why: string | null;
  angle: string | null;
  draft_text: string | null;
  products: PatrolProduct[];
}

interface Props {
  // Called after 我回了 saves a log, so the page can refresh its counts.
  onLogged: () => void;
  // Adds the staff member's utm params, same as the 記錄 tab's link builder.
  linkFor: (url: string) => string;
}

// Catalog photos are .webp, which Threads won't take as an attachment from every
// phone. Re-encode to JPEG in the browser (the CDN sends Access-Control-Allow-Origin: *),
// then open the share sheet — 儲存影像 or straight into Threads — or fall back to a
// download on desktop.
async function saveImage(src: string, name: string): Promise<'shared' | 'downloaded'> {
  const blob = await fetch(src).then(r => {
    if (!r.ok) throw new Error(`image ${r.status}`);
    return r.blob();
  });
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
  const jpeg: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.92)
  );
  const file = new File([jpeg], `${name}.jpg`, { type: 'image/jpeg' });
  const nav: any = navigator;
  if (nav.canShare?.({ files: [file] })) {
    await nav.share({ files: [file] });
    return 'shared';
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(a.href);
  return 'downloaded';
}

function apiMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || err?.response?.data?.message || fallback;
}

function ageLabel(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} 分鐘前`;
  if (hours < 24) return `${Math.round(hours)} 小時前`;
  return `${Math.round(hours / 24)} 天前`;
}

const ThreadsPatrolOpportunities: React.FC<Props> = ({ onLogged, linkFor }) => {
  const [opps, setOpps] = useState<PatrolOpportunity[]>([]);
  // Edited drafts, by post_key. Untouched cards fall back to the screener's text.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);

  const draftOf = (o: PatrolOpportunity) => drafts[o.post_key] ?? o.draft_text ?? '';

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(label);
    } catch {
      setToast('複製失敗，請長按選取');
    }
  };

  const save = async (p: PatrolProduct) => {
    if (!p.image) return;
    try {
      const how = await saveImage(p.image, `metanoia_${p.id}`);
      if (how === 'downloaded') setToast('圖片已下載');
    } catch (err: any) {
      // Closing the share sheet rejects with AbortError — not a failure.
      if (err?.name !== 'AbortError') setToast('存圖失敗，請長按圖片儲存');
    }
  };
  const [lastScan, setLastScan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/threads-patrol/opportunities');
      setOpps(res.data?.opportunities || []);
      setLastScan(res.data?.last_scan || null);
      setError(null);
    } catch (err: any) {
      setError(apiMessage(err, '讀取機會失敗，請重新整理'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async (key: string) => {
    // Optimistic: the card leaves at once; a failed call puts the list back.
    setOpps(prev => prev.filter(o => o.post_key !== key));
    try {
      await api.post(`/api/threads-patrol/opportunities/${encodeURIComponent(key)}/dismiss`);
    } catch (err: any) {
      setError(apiMessage(err, '標記失敗'));
      load();
    }
  };

  const answered = async (o: PatrolOpportunity) => {
    // Optimistic like 不適合. A 409 means a colleague already logged this post —
    // it is answered either way, so the card stays gone.
    setOpps(prev => prev.filter(x => x.post_key !== o.post_key));
    try {
      await api.post('/api/threads-patrol/logs', {
        post_url: o.post_url,
        reply_text: draftOf(o),
        post_excerpt: (o.post_text || '').replace(/\s+/g, ' ').slice(0, 80) || undefined
      });
      setToast('已記錄');
      onLogged();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setToast('同事已經回過這篇');
        return;
      }
      setError(apiMessage(err, '記錄失敗，請再試一次'));
      load();
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {opps.length} 則可以回
          {lastScan && <> · 上次掃描 {ageLabel(lastScan)}</>}
        </Typography>
        <Button size="small" onClick={load}>
          重新整理
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {opps.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          目前沒有新的機會。系統每兩小時會自動掃一次。
        </Typography>
      )}

      <Stack spacing={1.5}>
        {opps.map(o => {
          const text = o.post_text || '';
          const long = text.length > 140;
          const open = expanded[o.post_key];
          return (
            <Paper key={o.post_key} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                <Chip
                  label={o.score >= 3 ? '優先' : '可以回'}
                  size="small"
                  color={o.score >= 3 ? 'error' : 'default'}
                />
                {o.opp_type && <Chip label={o.opp_type} size="small" variant="outlined" />}
                {o.kind === 'comment' && <Chip label="留言" size="small" variant="outlined" />}
                <Typography variant="caption" color="text.secondary">
                  {ageLabel(o.posted_at)} · ♥{o.like_count} · 💬{o.reply_count}
                  {o.post_author && <> · @{o.post_author}</>}
                </Typography>
              </Stack>

              {(o.shop_reply_count ?? 0) > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mb: 0.5 }}>
                  已有 {o.shop_reply_count} 家店來留言推自己家——先好好回答問題，才不會被當成同一種。
                </Typography>
              )}

              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                onClick={() => long && setExpanded(prev => ({ ...prev, [o.post_key]: !open }))}
              >
                {long && !open ? `${text.slice(0, 140)}…` : text}
              </Typography>

              {o.draft_text ? (
                <Box sx={{ mt: 1.25 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="回覆草稿（可以改）"
                    value={draftOf(o)}
                    onChange={e => setDrafts(prev => ({ ...prev, [o.post_key]: e.target.value }))}
                  />
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => copy(draftOf(o), '回覆已複製')}>
                    複製回覆
                  </Button>
                </Box>
              ) : o.angle ? (
                <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  💡 {o.angle}
                </Typography>
              ) : null}

              {o.products?.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {o.products.map(p => (
                    <Stack key={p.id} direction="row" spacing={1.25} alignItems="center">
                      {p.image && (
                        <Box
                          component="img"
                          src={p.image}
                          alt={p.title}
                          sx={{ width: 56, height: 75, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{p.title}</Typography>
                        <Typography variant="caption" color="text.secondary">NT${p.price}</Typography>
                        <Stack direction="row" spacing={0.5}>
                          {p.image && (
                            <Button size="small" onClick={() => save(p)}>存圖</Button>
                          )}
                          <Button size="small" onClick={() => copy(linkFor(p.url), '連結已複製')}>
                            複製連結
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                <Button
                  size="small"
                  variant="contained"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  href={o.post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  開貼文
                </Button>
                <Button size="small" variant="outlined" onClick={() => answered(o)}>
                  我回了
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button size="small" color="inherit" onClick={() => dismiss(o.post_key)}>
                  不適合
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ThreadsPatrolOpportunities;
